/**
 * Embedded Settings: server response shapes <-> the tab's view model.
 *
 * The tab edits six Aelo fields and two destination switches; the server holds
 * the whole `generic_field_config` and one `destination` per CRM provider. These
 * helpers read the real responses of
 * `GET /organizations/:orgId/delivery/config` and `GET|POST /bitrix24/fields`
 * (schemas in `@aica/api-types`) and build the real PATCH body — nothing is
 * recomputed on the client that the server already says (field ids come from
 * the server's answer).
 */
import { DEFAULT_GENERIC_FIELD_CONFIG } from '@aica/core';
import type {
  CreateAicaFieldsResponse,
  CrmProviderName,
  GenericFieldConfigPatch,
  ListAicaFieldsResponse,
  OrgCrmProvidersPatch,
  OrgDeliveryConfigPatchResponse,
  OrgDeliveryConfigResponse,
} from '@aica/api-types';
import type {
  DeliveryConfig,
  DeliveryConfigResponse,
  FieldStatus,
  SmartProcess,
} from '../types';

/** The Aelo fields the embedded tab can switch on and off. */
export const EMBEDDED_FIELD_KEYS = [
  'transcript',
  'summary',
  'qualityScore',
  'saleProbability',
  'keywords',
  'sentiment',
] as const satisfies ReadonlyArray<keyof DeliveryConfig['fields']>;

/** Labels for a field row whose status could not be read (the server's own English ones). */
const FIELD_LABELS: Record<(typeof EMBEDDED_FIELD_KEYS)[number], string> = {
  transcript: 'Transcript',
  summary: 'Summary',
  qualityScore: 'Quality Score',
  saleProbability: 'Sale Probability',
  keywords: 'Keywords',
  sentiment: 'Sentiment',
};

/**
 * This screen is the Bitrix24 app: it edits the Bitrix24 provider, whichever CRM
 * the organization has made active. The field-status and create calls it makes
 * are Bitrix24's, so the destination it shows must be too.
 */
const PROVIDER = 'bitrix24' as const satisfies CrmProviderName;

/**
 * The PATCH body: only what the tab changed (#3001). The server merges
 * `generic_field_config` and the provider key by key, so the tab — whose cache
 * can be minutes old next to the web step «Обновления CRM» — never sends a copy
 * of a flag, a field mapping or the provider it did not touch.
 */
export interface DeliveryPatchBody {
  /** Only when the organization has no active CRM yet: the first config makes Bitrix24 it. */
  activeCrmProvider?: CrmProviderName;
  crmProviders?: OrgCrmProvidersPatch;
  genericFieldConfig?: GenericFieldConfigPatch;
}

/** The test body: the whole configuration the tab would deliver with. */
export interface DeliveryTestBody {
  activeCrmProvider?: CrmProviderName;
  crmProviders: NonNullable<OrgDeliveryConfigResponse['crmProviders']>;
  genericFieldConfig: NonNullable<OrgDeliveryConfigResponse['genericFieldConfig']>;
}

/**
 * Single source of the destination: the explicit one, else the legacy flags the
 * delivery worker also honours (`smartProcessEnabled` with a Smart Process id).
 * No config at all is the card — what a first save writes.
 */
function destinationOf(
  providerConfig: { destination?: 'crm_card' | 'smart_process' } & {
    smartProcessEnabled?: boolean;
    smartProcessId?: string | null;
  }
): 'crm_card' | 'smart_process' {
  if (providerConfig.destination) return providerConfig.destination;
  return providerConfig.smartProcessEnabled && providerConfig.smartProcessId
    ? 'smart_process'
    : 'crm_card';
}

/**
 * The tab's state for what the server returned. An organization with no
 * Bitrix24 provider or no field config yet is not an error: the screen shows the
 * defaults (the card as destination, the shared field defaults) and a save writes
 * the first config — a dead end here would leave an admin no way to create it.
 */
export function toDeliveryView(
  source: OrgDeliveryConfigResponse,
  smartProcessTitle: string
): DeliveryConfigResponse {
  const fieldConfig = { ...DEFAULT_GENERIC_FIELD_CONFIG, ...source.genericFieldConfig };
  const providerConfig = source.crmProviders[PROVIDER]?.config ?? {};
  const destination = destinationOf(providerConfig);
  const smartProcessId = providerConfig.smartProcessId ?? '';

  const fields = Object.fromEntries(
    EMBEDDED_FIELD_KEYS.map((key) => [key, fieldConfig[key]])
  ) as DeliveryConfig['fields'];

  const smartProcesses: SmartProcess[] = smartProcessId
    ? [
        {
          id: smartProcessId,
          title: smartProcessTitle,
          entityTypeId: Number.parseInt(smartProcessId, 10) || 0,
        },
      ]
    : [];

  return {
    config: {
      deliverToCrmCard: destination === 'crm_card',
      deliverToSmartProcess: destination === 'smart_process',
      smartProcessId,
      fields,
    },
    smartProcesses,
    recentDeliveries: source.recentDeliveries,
    source,
  };
}

/** Field rows as the server reports them. */
export function toFieldStatuses(fieldList: ListAicaFieldsResponse): FieldStatus[] {
  return fieldList.fields.map((field) => ({
    name: field.name,
    label: field.label,
    created: field.created,
    fieldId: field.fieldId,
  }));
}

/** Field rows for a status that could not be read: the six fields, none claimed created. */
export function unknownFieldStatuses(): FieldStatus[] {
  return EMBEDDED_FIELD_KEYS.map((name) => ({
    name,
    label: FIELD_LABELS[name],
    created: false,
  }));
}

/**
 * The destination the tab's switches name. The server stores ONE destination and
 * the tab keeps the two switches mutually exclusive, so the Smart Process switch
 * alone decides (a card-only or an empty selection both write `crm_card`; the tab
 * refuses to save the empty one). Switching the Smart Process off clears the
 * tab's id, not the stored one: the selectable list is built from the stored id,
 * so dropping it here would leave nothing to switch back to.
 */
function destinationOfTab(
  config: DeliveryConfig,
  storedSmartProcessId: string | null | undefined
): { destination: 'crm_card' | 'smart_process'; smartProcessId: string | null } {
  return {
    destination: config.deliverToSmartProcess ? 'smart_process' : 'crm_card',
    smartProcessId: config.smartProcessId || storedSmartProcessId || null,
  };
}

/**
 * The PATCH body for the tab's state — only what differs from what the tab was
 * loaded with (#3001): the changed switches; the destination when it changed.
 * An organization with no field config yet gets one too, but still only the
 * changed switches (#3025): the server writes the shared defaults under them,
 * and a config another writer created between the tab's GET and this PATCH
 * keeps its flags. A Bitrix24 provider never stored is created enabled; an
 * organization with no active CRM gets Bitrix24 as it — without that the
 * server would still call it unconfigured.
 *
 * `loadedFrom` is the server state the switches were hydrated from. A refetch
 * with unsaved switches moves `source` but leaves the switches alone, so a
 * switch the admin never touched would differ from the newer `source` — and,
 * compared with it, revert what another writer saved meanwhile.
 */
export function toDeliveryPatchBody(
  source: OrgDeliveryConfigResponse,
  config: DeliveryConfig,
  loadedFrom: OrgDeliveryConfigResponse = source
): DeliveryPatchBody {
  const loaded = toDeliveryView(loadedFrom, '').config;
  const stored = source.crmProviders[PROVIDER];
  const body: DeliveryPatchBody = {};

  if (source.activeCrmProvider === null) body.activeCrmProvider = PROVIDER;

  const destinationChanged =
    config.deliverToSmartProcess !== loaded.deliverToSmartProcess ||
    (config.deliverToSmartProcess && config.smartProcessId !== loaded.smartProcessId);
  if (!stored || destinationChanged) {
    body.crmProviders = {
      [PROVIDER]: {
        ...(stored ? {} : { enabled: true }),
        config: destinationOfTab(config, stored?.config.smartProcessId),
      },
    };
  }

  // Compared with the defaults when nothing was stored: that is what the tab
  // showed, and what the server merges a first partial config over.
  const changed = EMBEDDED_FIELD_KEYS.filter(
    (key) => config.fields[key] !== loaded.fields[key]
  );
  if (changed.length > 0 || source.genericFieldConfig === null) {
    body.genericFieldConfig = Object.fromEntries(
      changed.map((key) => [key, config.fields[key]])
    );
  }

  return body;
}

/**
 * The test body for the tab's state: the whole `generic_field_config` (the
 * server's, else the shared defaults) with the six switches overlaid, and the
 * Bitrix24 provider with the tab's destination — what delivery would run with.
 */
export function toDeliveryTestBody(
  source: OrgDeliveryConfigResponse,
  config: DeliveryConfig
): DeliveryTestBody {
  const provider = source.crmProviders[PROVIDER] ?? {
    enabled: true,
    field_mappings: {},
    config: {},
  };

  return {
    ...(source.activeCrmProvider === null ? { activeCrmProvider: PROVIDER } : {}),
    crmProviders: {
      [PROVIDER]: {
        ...provider,
        config: {
          ...provider.config,
          ...destinationOfTab(config, provider.config.smartProcessId),
        },
      },
    },
    genericFieldConfig: {
      ...DEFAULT_GENERIC_FIELD_CONFIG,
      ...source.genericFieldConfig,
      ...config.fields,
    },
  };
}

/**
 * The source after a successful save: the config the server answered with,
 * merged and stored (#3025) — not the tab's cached GET with the body laid over
 * it, which would show a flag another writer changed meanwhile at its stale
 * value. The PATCH answer carries no deliveries; those stay as loaded.
 */
export function toSavedSource(
  source: OrgDeliveryConfigResponse,
  stored: OrgDeliveryConfigPatchResponse
): OrgDeliveryConfigResponse {
  return { ...stored, recentDeliveries: source.recentDeliveries };
}

/**
 * The switches after a save that the admin kept editing while it was in
 * flight: what they changed after clicking Save (`current` differs from
 * `submitted`) stays, everything else shows the saved state. The kept edits
 * remain unsaved — they differ from `saved`, so the tab stays dirty.
 */
export function rebaseEditsOnSaved(
  saved: DeliveryConfig,
  submitted: DeliveryConfig,
  current: DeliveryConfig
): DeliveryConfig {
  if (current === submitted) return saved;
  const pick = <K extends keyof DeliveryConfig>(key: K): DeliveryConfig[K] =>
    current[key] !== submitted[key] ? current[key] : saved[key];
  const fields = { ...saved.fields };
  for (const key of EMBEDDED_FIELD_KEYS) {
    if (current.fields[key] !== submitted.fields[key]) fields[key] = current.fields[key];
  }
  return {
    deliverToCrmCard: pick('deliverToCrmCard'),
    deliverToSmartProcess: pick('deliverToSmartProcess'),
    smartProcessId: pick('smartProcessId'),
    fields,
  };
}

/**
 * Field statuses after POST /bitrix24/fields: the server says which fields are
 * ready on every card type (`created` + `existing`) and under which Bitrix24
 * name; a field it reports failed keeps its earlier status.
 */
export function applyCreateFieldsResult(
  statuses: FieldStatus[],
  response: Pick<CreateAicaFieldsResponse, 'created' | 'existing'>
): FieldStatus[] {
  const ready = new Map(
    [...response.created, ...response.existing].map((field) => [
      field.name,
      field.fieldId,
    ])
  );
  return statuses.map((status) =>
    ready.has(status.name)
      ? { ...status, created: true, fieldId: ready.get(status.name) }
      : status
  );
}
